import React, { useState } from 'react';
import FilterPopover from "../FilterPopover";
import FilterCheckBox from "../FilterCheckBox";
import { Checkbox, DatePicker, Divider, Form } from "antd";
import { FormattedMessage } from "react-intl";
import { useProgram } from "hooks/Program/useProgram";
import { Button } from "ui";

type FilterDataMap = Record<string, unknown>;

interface LearningProgramFilterInterface {
    tagBuilder: Function;
    setFilterValues: any;
    filterValues: any
    form: any;
    filterKey: string;
    setFilterKey: React.Dispatch<React.SetStateAction<string>>;
}

const LearningProgramFilter: React.FC<LearningProgramFilterInterface> = ({
    filterKey,
    setFilterKey,
    form,
    tagBuilder,
    setFilterValues,
}) => {
    const { searchProgram, loading } = useProgram();
    const [data, setData] = useState<FilterDataMap>({}); // latest search results
    const [selected, setSelected] = useState<FilterDataMap>({}); // chosen programs, kept across searches
    const [initialValues, setInitialValues] = useState(undefined)

    const key = 'learning-program'

    const onSearch = async (value: string = '') => {
        const response = await searchProgram(value);
        const safeResponse: FilterDataMap =
            response && typeof response === 'object' && !Array.isArray(response) ? response : {};
        setData(safeResponse);

        setFilterValues((prev: any) => ({
            ...prev,
            program: {
                ...prev.program,
                ...selected,
                ...safeResponse,
            },
        }));
    };

    // antd's Checkbox.Group drops any value whose <Checkbox> is not currently mounted,
    // so the chosen programs must stay rendered across searches.
    const onSelectionChange = (ids: string[]) => {
        const known: FilterDataMap = { ...selected, ...data };
        const nextSelected = ids.reduce<FilterDataMap>((acc, id) => {
            if (id in known) acc[id] = known[id];
            return acc;
        }, {});

        setSelected(nextSelected);
        setFilterValues((prev: any) => ({
            ...prev,
            program: {
                ...prev.program,
                ...nextSelected,
            },
        }));
    };

    const content = (
        <div className={'flex flex-column p-1 checkbox-wrapper'}>
            <FilterCheckBox
                data={{ ...selected, ...data }}
                setData={onSelectionChange}
                name={'program'}
                onSearch={onSearch}
            />
            <div className={'p-1 checkbox-wrapper'}>
                <div className={'bold'}>
                    <FormattedMessage id={'general.status'} />
                </div>
                <Form.Item name={'programStatus'}>
                    <Checkbox.Group className={'flex'}>
                        <Checkbox value="COMPLETED"><FormattedMessage id='general.completed' /></Checkbox>
                        <Checkbox value="IN_PROGRESS"><FormattedMessage id='general.in_progress' /></Checkbox>
                        <Checkbox value="NOT_STARTED"><FormattedMessage id='general.not_started' /></Checkbox>
                        <Checkbox value="FAILED"><FormattedMessage id='general.failed' /></Checkbox>
                    </Checkbox.Group>
                </Form.Item>
                <Divider className={'m-2'} />
                <div className={'bold'}>
                    <FormattedMessage id={'general.is_cancelled'} />
                </div>
                <Form.Item name={'isCancelled'}>
                    <Checkbox.Group className={'flex'}>
                        <Checkbox value={1}><FormattedMessage id='general.yes' /></Checkbox>
                        <Checkbox value={0}><FormattedMessage id='general.no' /></Checkbox>
                    </Checkbox.Group>
                </Form.Item>
                <Divider className={'m-2'} />
                <div className={'bold'}>
                    <FormattedMessage id={'general.execution_status'} />
                </div>
                <Form.Item name={'completionStatus'}>
                    <Checkbox.Group className={'flex'}>
                        <Checkbox value='IN_PROGRESS'><FormattedMessage id='general.in_progress' /></Checkbox>
                        <Checkbox value='OVERDUE_VALIDITY'><FormattedMessage id='general.overdue_validity' /></Checkbox>
                        <Checkbox value='EXECUTED_ON_TIME'><FormattedMessage id='general.executed_on_time' /></Checkbox>
                        <Checkbox value='EXECUTED_LATE'><FormattedMessage id='general.executed_late' /></Checkbox>
                    </Checkbox.Group>
                </Form.Item>
                <Divider className={'m-2'} />
                <div className={'bold'}>
                    <FormattedMessage id={'general.last_activity'} />
                </div>
                <div className={'flex flex-column checkbox-wrapper'}>
                    <Form.Item name={'lastActivityDate'}>
                        <DatePicker.RangePicker />
                    </Form.Item>
                </div>
                <Divider className={'m-2'} />
            </div>
            <div className={'flex justify-between p-1'}>
                <Button type={'link'} onClick={() => handleClose()} message='general.cancel' />
                <Button type={'primary'} onClick={() => {setFilterKey(''); handleOpen(false); }} message='general.select' />
            </div>
        </div>
    );

    const handleClose = () => {
        form.setFieldsValue(initialValues);
        setFilterKey('');
    }

    const handleOpen = (open: boolean) => {
        if (open) {
            setInitialValues(form.getFieldsValue())
            setFilterValues((prev: any) => ({
                ...prev,
                program: {
                    ...prev.program,
                    ...selected,
                    ...data,
                },
            }));
        } else {
            tagBuilder()
        }
    }

    return (
        <FilterPopover
            popoverKey={key}
            filterKey={filterKey}
            setOpen={setFilterKey}
            placement={'bottomLeft'}
            handleOpen={handleOpen}
            content={content}
            loading={loading}
            title={'general.programs'}
            buttonTitle={'general.programs'}
        />
    )
}

export default LearningProgramFilter;